import { attribute, attributeGetter, Component, type Emits } from "@bodil/dom/component";
import { applyUniqueID } from "@bodil/dom/dom";
import type { Role } from "aria-attribute-types";
import { html } from "lit";
import { customElement } from "lit/decorators.js";

import { RippleController } from "./controllers/ripple";
import { KeyboardCommand, keymapLookup } from "./keymap";

import styles from "./tab.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-tab": BtkTab;
    }
}

@customElement("btk-tab")
export class BtkTab extends Component {
    emits!: Emits<"change">;

    @attribute({ type: Boolean }) accessor active = false;

    @attributeGetter({ type: Number, reactive: true }) get tabindex(): number {
        return this.active ? 0 : -1;
    }

    @attributeGetter get role(): Role {
        return "tab";
    }

    @attributeGetter({ reactive: true }) get ariaSelected(): string | null {
        return this.active ? "true" : null;
    }

    private readonly rippleController = new RippleController(this);

    constructor() {
        super();
        applyUniqueID(this);
    }

    protected override connected() {
        super.connected();
        this.useWhileConnected(
            this.on("click", (e: Event) => {
                e.preventDefault();
                this.emit("change");
            }),
            this.on("keydown", (e: KeyboardEvent) => {
                const cmd = keymapLookup(e);
                if (cmd === KeyboardCommand.Enter) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.emit("change");
                }
            }),
            this.on("mousedown", (event: MouseEvent) =>
                this.rippleController.playRipple(this, event),
            ),
        );
    }

    protected override render(): unknown {
        return html`<slot></slot>`;
    }

    static styles = styles;
}
